import React, { useState } from 'react';
import { Box, Button, TextField, Typography, Link, Alert } from '@mui/material';
import AuthLayout from '../../components/auth/AuthLayout';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useNotification } from '../../contexts/NotificationContext';
import { ERROR_MESSAGES, getFirebaseErrorMessage } from '../../utils/errorMessages';
import { sendEmailVerification } from 'firebase/auth';
import { isDisposableEmail } from '../../utils/disposableEmailDomains';

function Register() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [errors, setErrors] = useState({
        email: '',
        password: '',
        confirmPassword: ''
    });
    const { register } = useAuth();
    const navigate = useNavigate();
    const { showNotification } = useNotification();

    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            return false;
        }

        // 捨てアドレスチェック
        if (isDisposableEmail(email)) {
            return false;
        }

        return true;
    };

    const validatePassword = (password) => {
        const minLength = 8;
        const hasNumber = /\d/.test(password);
        const hasLetter = /[a-zA-Z]/.test(password);

        if (password.length < minLength) {
            return ERROR_MESSAGES.VALIDATION.PASSWORD.MIN_LENGTH;
        }
        if (!hasNumber || !hasLetter) {
            return ERROR_MESSAGES.VALIDATION.PASSWORD.REQUIREMENTS;
        }
        return null;
    };

    const handleEmailChange = (e) => {
        const newEmail = e.target.value;
        setEmail(newEmail);

        if (!newEmail) {
            setErrors(prev => ({
                ...prev,
                email: ERROR_MESSAGES.VALIDATION.REQUIRED
            }));
        } else if (!validateEmail(newEmail)) {
            // 捨てアドレスチェック
            if (isDisposableEmail(newEmail)) {
                setErrors(prev => ({
                    ...prev,
                    email: ERROR_MESSAGES.AUTH.DISPOSABLE_EMAIL
                }));
            } else {
                setErrors(prev => ({
                    ...prev,
                    email: ERROR_MESSAGES.VALIDATION.EMAIL
                }));
            }
        } else {
            setErrors(prev => ({
                ...prev,
                email: ''
            }));
        }
    };

    const handlePasswordChange = (e) => {
        const newPassword = e.target.value;
        setPassword(newPassword);
        const passwordError = validatePassword(newPassword);
        setErrors(prev => ({
            ...prev,
            password: !newPassword ? ERROR_MESSAGES.VALIDATION.REQUIRED : passwordError || ''
        }));
    };

    const handleConfirmPasswordChange = (e) => {
        const newConfirmPassword = e.target.value;
        setConfirmPassword(newConfirmPassword);
        setErrors(prev => ({
            ...prev,
            confirmPassword: !newConfirmPassword ? ERROR_MESSAGES.VALIDATION.REQUIRED :
                newConfirmPassword !== password ? ERROR_MESSAGES.VALIDATION.PASSWORD.MATCH : ''
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // 全フィールドのバリデーション
        let emailError = '';
        if (!email) {
            emailError = ERROR_MESSAGES.VALIDATION.REQUIRED;
        } else if (!validateEmail(email)) {
            // 捨てアドレスチェック
            if (isDisposableEmail(email)) {
                emailError = ERROR_MESSAGES.AUTH.DISPOSABLE_EMAIL;
            } else {
                emailError = ERROR_MESSAGES.VALIDATION.EMAIL;
            }
        }

        const newErrors = {
            email: emailError,
            password: !password ? ERROR_MESSAGES.VALIDATION.REQUIRED :
                validatePassword(password) || '',
            confirmPassword: !confirmPassword ? ERROR_MESSAGES.VALIDATION.REQUIRED :
                confirmPassword !== password ? ERROR_MESSAGES.VALIDATION.PASSWORD.MATCH : ''
        };

        setErrors(newErrors);

        // エラーがある場合は処理を中断
        if (Object.values(newErrors).some(error => error)) {
            return;
        }

        let userCredential = null;
        let token = null;

        try {
            // Firebase認証
            userCredential = await register(email, password);

            if (!userCredential?.user) {
                throw new Error(ERROR_MESSAGES.AUTH.FIREBASE_AUTH_FAILED);
            }

            // メール認証メールを送信
            await sendEmailVerification(userCredential.user);

            token = await userCredential.user.getIdToken();
            if (!token) {
                throw new Error(ERROR_MESSAGES.AUTH.TOKEN_FETCH_FAILED);
            }

            // MongoDBにユーザーを作成
            const response = await fetch('/api/users/register', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    email: email,
                    emailVerified: false
                })
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || ERROR_MESSAGES.AUTH.USER_CREATE_FAILED);
            }

            showNotification('アカウントを作成しました。メールアドレスの確認メールを送信しましたので、メールを確認してください。', 'success');
            navigate('/email-verification');
        } catch (error) {
            console.error('Registration error:', error);

            // Firebaseのエラーメッセージを日本語化
            const errorMessage = error.code ? getFirebaseErrorMessage(error) : (error.message || ERROR_MESSAGES.AUTH.REGISTER_FAILED);
            showNotification(errorMessage, 'error');

            // Firebaseユーザーが作成されていた場合は削除を試みる
            if (userCredential?.user) {
                try {
                    await userCredential.user.delete();
                    console.log(ERROR_MESSAGES.FORM.FIREBASE_USER_DELETE_SUCCESS);
                } catch (deleteError) {
                    console.error(ERROR_MESSAGES.FORM.FIREBASE_USER_DELETE_ERROR, deleteError);
                }
            }
        }
    };

    return (
        <AuthLayout>
            <Typography variant="h5" component="h1" align="center" gutterBottom>
                新規登録
            </Typography>
            <Box component="form" onSubmit={handleSubmit} noValidate>
                <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="email"
                    label="メールアドレス"
                    name="email"
                    autoComplete="email"
                    autoFocus
                    value={email}
                    onChange={handleEmailChange}
                    error={!!errors.email}
                    helperText={errors.email}
                />
                <TextField
                    margin="normal"
                    required
                    fullWidth
                    name="password"
                    label="パスワード"
                    type="password"
                    id="password"
                    value={password}
                    onChange={handlePasswordChange}
                    error={!!errors.password}
                    helperText={errors.password}
                />
                <TextField
                    margin="normal"
                    required
                    fullWidth
                    name="confirmPassword"
                    label="パスワード（確認）"
                    type="password"
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={handleConfirmPasswordChange}
                    error={!!errors.confirmPassword}
                    helperText={errors.confirmPassword}
                />
                <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    sx={{ mt: 3, mb: 2 }}
                >
                    登録
                </Button>
                <Box sx={{ textAlign: 'center' }}>
                    <Link href="/login" variant="body2">
                        すでにアカウントをお持ちの方はこちら
                    </Link>
                </Box>
            </Box>
        </AuthLayout>
    );
}

export default Register; 